import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  Plus, RefreshCw, Monitor, Smartphone, Box, MoreHorizontal,
  Pencil, Trash2, ChevronRight, ChevronLeft, Search,
  Wifi, WifiOff, BadgeCheck, HardDrive, FileText, Film, Music, Image as ImageIcon,
  Type as TypeIcon, Activity as ActivityIcon, Globe, Upload, ChevronDown,
  Users, MonitorPause, Sparkles, Loader2, MapPin, Info,
} from 'lucide-react'
import { Button } from '@meno/ui/components/ui/button'
import { Input } from '@meno/ui/components/ui/input'
import { Skeleton } from '@meno/ui/components/ui/skeleton'
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from '@meno/ui/components/ui/dropdown-menu'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@meno/ui/components/ui/dialog'
import { Progress } from '@meno/ui/components/ui/progress'
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@meno/ui/components/ui/select'
import { ConfirmDialog } from '@meno/ui/components/ui/confirm-dialog'
import {
  getScreens, getDashboardStats, getMedia,
  getScreenGroups, createScreenGroup, updateScreenGroup, deleteScreenGroup,
  getAppSubscription,
  createPlaylist, createAudioPlaylist,
  getAudios, getAudioPlaylists, getStopScreens,
} from '@meno/ui/lib/api'
import { formatBytes } from '@meno/ui/lib/utils'
import { useBranchFilter } from '@meno/ui/contexts/BranchFilterContext'
import { usePageHeader } from '@meno/ui/components/layout/PageHeader'
import { usePermissions } from '@meno/ui/hooks/usePermissions'
import { TabButton } from '@meno/ui/components/shared/TabButton'
import { ScreenStatusBadge } from '@meno/ui/components/shared/ScreenStatusBadge'
import ScreenGroupDialog, { type GroupScreen } from '@/components/ScreenGroupDialog'
import ScreensEmptyState from '@/components/ScreensEmptyState'
import ScreenInfoDrawer from '@/components/ScreenInfoDrawer'
import PlaylistContentPicker, { type PickedItem } from '@/components/PlaylistContentPicker'
import { WEB_PLAYER_URL, WEB_PLAYER_HOST } from '@/lib/webPlayer'
import { PublishDialog, type PublishPayload } from '@/components/PublishDialog'
import {
  publishQuickTarget, pickedItemToTarget, publishDialogKind, type QuickPublishTarget,
} from '@/lib/quickPublish'
import { TRANSITIONS } from '@/lib/transitions'
import { useTaskCenter } from '@/contexts/TaskCenterContext'
import { useUpload } from '@/contexts/UploadContext'
import { TaskRow } from '@/components/TaskCenter/taskRows'

// Storage quota per org until the backend exposes a real plan limit. Sized to
// match the Media Hub's messaging; the Storage card's bar/percentage are all
// derived from this single constant so it's a one-line change when wired up.
const STORAGE_QUOTA_BYTES = 5 * 1024 * 1024 * 1024 // 5 GB

// The browser-based signage player (the PWA that runs a screen in a tab). The
// Globe action in the header opens it so operators can preview / run a display
// without installing the Android APK. The origin is environment-resolved (see
// lib/webPlayer) — a staging dashboard must hand out the STAGING player, since
// a pairing code from the production player is issued by a different backend.

// Screens / groups are paged (not scrolled) so the panel keeps a stable height
// no matter how many the org has. 6 = two rows on the lg 3-col grid — small
// enough that the whole dashboard fits without a page-level scrollbar.
const PAGE_SIZE = 6

// ── Types ─────────────────────────────────────────────────────────────────
type DashStats = {
  total_screens: number; online_screens: number; offline_screens: number
  idle_screens?: number
}

type MediaItem = {
  id: number
  original_name: string
  file_type: string
  file_size?: number | null
  url: string
  variants?: Record<string, string> | null
  created_at: string
}

type ScreenItem = GroupScreen & {
  public_id: string
  branch_id?: number
  last_seen?: string
  // License expiry (mirror of the active license's expires_at). Drives the
  // "Expiring"/"Expired" status badge. NULL = perpetual / no license.
  disconnect_at?: string | null
}

type ScreenGroupRow = {
  id: number
  name: string
  description?: string
  screens: { id: number; name: string; status?: string }[]
  online_count: number
  total_count: number
  created_at: string
}

// The extra Quick Publish flavours that need their own single-item picker
// (content reuses the existing PlaylistContentPicker).
type QuickPickKind = 'audio' | 'stop_screen' | 'transition'

// ── Page ──────────────────────────────────────────────────────────────────
// Operator-style dashboard: top action cards for adding a screen / buying
// licences / monitoring totals, and a tabbed body with screen groups +
// the raw screen list. Replaces the chart-heavy version at /overview.
export default function DashboardPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { can, fullAccess } = usePermissions()
  const canCreateScreen = can('screens:create')
  const canEditScreens = can('screens:edit')       // screen groups live under SCREENS_EDIT server-side
  const canUploadMedia = can('media:upload')
  const canCreatePlaylists = can('playlists:create')
  const canPublish = can('playlists:edit')   // pushing content to a screen is an edit action
  const canQuickAdd = canCreateScreen || canEditScreens || canUploadMedia || canCreatePlaylists
  usePageHeader({ title: t('dashboard.title', 'Dashboard') })

  const { tasks } = useTaskCenter()

  const [stats, setStats] = useState<DashStats | null>(null)
  const [screens, setScreens] = useState<ScreenItem[]>([])
  const [groups, setGroups] = useState<ScreenGroupRow[]>([])
  const [media, setMedia] = useState<MediaItem[]>([])
  const [statsLoading, setStatsLoading] = useState(true)
  const [groupsLoading, setGroupsLoading] = useState(true)
  const [mediaLoading, setMediaLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)

  const [tab, setTab] = useState<'groups' | 'screens'>('screens')
  const [groupSearch, setGroupSearch] = useState('')
  // Branch scope for the whole dashboard (stat cards + screens/groups panel).
  // Options come from the shared sidebar branch filter's accessible list.
  const { branches, isEnabled } = useBranchFilter()
  const [filterBranch, setFilterBranch] = useState('all')
  const [screensPage, setScreensPage] = useState(1)
  const [groupsPage, setGroupsPage] = useState(1)

  const [createOpen, setCreateOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<ScreenGroupRow | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<ScreenGroupRow | null>(null)
  // Quick Add (create screen / group / playlist / audio playlist) + Quick
  // Publish (pick content/audio/stop screen/transition → push to
  // screens/groups) live in the header toolbar.
  const [quickBusy, setQuickBusy] = useState(false)
  const [publishPickerOpen, setPublishPickerOpen] = useState(false)
  const [quickPickKind, setQuickPickKind] = useState<QuickPickKind | null>(null)
  const [publishTarget, setPublishTarget] = useState<QuickPublishTarget | null>(null)
  // Screen whose right-side info drawer is open (null = closed) — same panel
  // the Screens page uses, reachable from each dashboard screen card.
  const [infoTarget, setInfoTarget] = useState<ScreenItem | null>(null)

  // ── Data loaders ────────────────────────────────────────────────────────
  const loadStatsAndScreens = useCallback(async () => {
    setStatsLoading(true)
    try {
      const [statsRes, screensRes] = await Promise.all([
        getDashboardStats(),
        getScreens(),
      ])
      setStats(statsRes.data)
      setScreens(screensRes.data)
    } catch {
      // Stats endpoint may not yet exist on fresh deployments; the cards
      // fall back to derived counts from /screens below.
    } finally {
      setStatsLoading(false)
    }
  }, [])

  const loadGroups = useCallback(async () => {
    setGroupsLoading(true)
    try {
      const { data } = await getScreenGroups()
      setGroups(data)
    } catch {
      toast.error(t('group.loadFailed', 'Failed to load groups'))
    } finally {
      setGroupsLoading(false)
    }
  }, [t])

  // Media powers both the Recent Assets list and the Storage Used card.
  const loadMedia = useCallback(async () => {
    setMediaLoading(true)
    try {
      const { data } = await getMedia(false, 'signage')
      setMedia(data)
    } catch {
      // Storage/recent-assets are best-effort; leave the cards empty on failure.
    } finally {
      setMediaLoading(false)
    }
  }, [])

  useEffect(() => { loadStatsAndScreens() }, [loadStatsAndScreens])
  useEffect(() => { loadGroups() },           [loadGroups])
  useEffect(() => { loadMedia() },            [loadMedia])

  // Branch scope — 'all' passes every screen the caller can access (isEnabled
  // narrows to the sidebar's accessible-branch set, same as ScreensPage);
  // otherwise only screens assigned to the selected branch. Groups pass when
  // they contain at least one screen of the branch (groups themselves aren't
  // branch-scoped). Without the isEnabled() pass, 'all' silently showed every
  // screen in the org regardless of the caller's own branch membership.
  const accessibleScreens = screens.filter(s => isEnabled(s.branch_id))
  const branchScreens = filterBranch === 'all'
    ? accessibleScreens
    : accessibleScreens.filter(s => s.branch_id?.toString() === filterBranch)
  const branchScreenIds = new Set(branchScreens.map(s => s.id))
  const branchGroups = filterBranch === 'all'
    ? groups
    : groups.filter(g => g.screens.some(s => branchScreenIds.has(s.id)))

  // Derived counts — prefer the live /screens list since it's freshest. The
  // stats endpoint is only a fallback for the unscoped view; branch-scoped
  // counts always come from the screen list.
  const totalScreens   = branchScreens.length || (filterBranch === 'all' ? stats?.total_screens : 0) || 0
  const onlineScreens  = branchScreens.filter(s => s.status === 'online').length
    || (filterBranch === 'all' ? stats?.online_screens : 0) || 0
  const offlineScreens = Math.max(0, totalScreens - onlineScreens)

  // Storage — summed from the live media library, capped against the quota.
  const storageUsed = media.reduce((sum, m) => sum + (m.file_size || 0), 0)
  const storagePct  = Math.min(100, Math.round((storageUsed / STORAGE_QUOTA_BYTES) * 100))

  // Recent assets (newest first) + recent activity for the dashboard sections.
  // Both cards page through the full lists internally, so we hand them the
  // complete sorted arrays rather than a fixed head slice.
  const recentAssets = [...media]
    .sort((a, b) => +new Date(b.created_at) - +new Date(a.created_at))
  const recentActivity = tasks

  // Live seat usage from /app-subscriptions/signage. Refetched on
  // every dashboard refresh, on mount, and on window focus — the last one
  // catches admin grants made while the user was on another tab.
  const [licenceSeats, setLicenceSeats] = useState({ used: 0, total: 0 })
  useEffect(() => {
    let cancelled = false
    const load = () => getAppSubscription('signage')
      .then(r => { if (!cancelled) setLicenceSeats({ used: r.data.seats_used, total: r.data.seat_count }) })
      .catch(() => { /* keep 0 — fails closed */ })
    load()
    const onFocus = () => load()
    window.addEventListener('focus', onFocus)
    return () => { cancelled = true; window.removeEventListener('focus', onFocus) }
  }, [])

  const handleRefresh = async () => {
    setRefreshing(true)
    await Promise.all([loadStatsAndScreens(), loadGroups(), loadMedia()])
    setRefreshing(false)
    toast.success(t('dashboard.refreshed', 'Refreshed'))
  }

  // ── Group mutations ─────────────────────────────────────────────────────
  const handleCreateGroup = async (data: { name: string; description: string; screen_ids: number[] }) => {
    try {
      const { data: created } = await createScreenGroup({
        name: data.name,
        description: data.description || undefined,
        screen_ids: data.screen_ids,
      })
      setGroups(prev => [created, ...prev])
      setCreateOpen(false)
      toast.success(t('group.created', 'Group created'))
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || t('group.createFailed', 'Failed to create group'))
      throw err
    }
  }

  const handleEditGroup = async (data: { name: string; description: string; screen_ids: number[] }) => {
    if (!editTarget) return
    try {
      const { data: updated } = await updateScreenGroup(editTarget.id, {
        name: data.name,
        description: data.description || undefined,
        screen_ids: data.screen_ids,
      })
      setGroups(prev => prev.map(g => g.id === updated.id ? updated : g))
      setEditTarget(null)
      toast.success(t('common.saved', 'Saved'))
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || t('common.updateFailed', 'Failed to update'))
      throw err
    }
  }

  const handleDeleteGroup = async () => {
    if (!deleteTarget) return
    try {
      await deleteScreenGroup(deleteTarget.id)
      setGroups(prev => prev.filter(g => g.id !== deleteTarget.id))
      toast.success(t('common.deleted', 'Deleted'))
    } catch {
      toast.error(t('common.updateFailed', 'Failed to update'))
    } finally {
      setDeleteTarget(null)
    }
  }

  const filteredGroups = groupSearch.trim()
    ? branchGroups.filter(g => g.name.toLowerCase().includes(groupSearch.toLowerCase()))
    : branchGroups
  const filteredScreens = groupSearch.trim()
    ? branchScreens.filter(s => s.name.toLowerCase().includes(groupSearch.toLowerCase()))
    : branchScreens

  // Reset both pagers to page 1 on a new search / branch change (done in the
  // handlers, not an effect). Slices are still clamped below so a deletion
  // can't strand the user on an out-of-range page.
  const onSearchChange = (value: string) => {
    setGroupSearch(value)
    setGroupsPage(1)
    setScreensPage(1)
  }
  const onBranchChange = (value: string) => {
    setFilterBranch(value)
    setGroupsPage(1)
    setScreensPage(1)
  }

  // Paged slices — page is clamped in case a deletion shrinks the list below
  // the current page.
  const groupsTotalPages  = Math.max(1, Math.ceil(filteredGroups.length / PAGE_SIZE))
  const gPage             = Math.min(groupsPage, groupsTotalPages)
  const pagedGroups       = filteredGroups.slice((gPage - 1) * PAGE_SIZE, gPage * PAGE_SIZE)
  const screensTotalPages = Math.max(1, Math.ceil(filteredScreens.length / PAGE_SIZE))
  const sPage             = Math.min(screensPage, screensTotalPages)
  const pagedScreens      = filteredScreens.slice((sPage - 1) * PAGE_SIZE, sPage * PAGE_SIZE)

  // ── Quick Add ──────────────────────────────────────────────────────────
  // Create an empty playlist / audio playlist and jump straight into its
  // editor (mirrors the Audio Hub's "New Playlist" flow). Screens keep using
  // the dedicated /screens/new wizard.
  const quickAddPlaylist = async () => {
    setQuickBusy(true)
    try {
      const { data } = await createPlaylist({ name: t('contentHub.newPlaylistName', 'New playlist') })
      navigate(`/management/media/playlists/${data.id}`)
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || t('common.error', 'Something went wrong'))
    } finally { setQuickBusy(false) }
  }
  const quickAddAudioPlaylist = async () => {
    setQuickBusy(true)
    try {
      const { data } = await createAudioPlaylist({ name: t('audioHub.newPlaylistName', 'New audio playlist') })
      navigate(`/audio-hub/playlists/${data.id}`)
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || t('common.error', 'Something went wrong'))
    } finally { setQuickBusy(false) }
  }

  // Quick Add uploads run through the app-wide universal upload modal — the
  // same Assets-page experience (per-type crop / PDF / audio, batch-aware) —
  // so there's no separate crop/upload flow to maintain here.
  const { openUpload } = useUpload()

  // ── Quick Publish ──────────────────────────────────────────────────────
  // Pick one item (content, audio, stop screen, or transition), then hand it
  // to the shared PublishDialog to select screens/groups. Routing to the
  // per-kind publish endpoint lives in the shared quick-publish service, so
  // this is the exact same call path as the Content Hub's "Publish content".
  const handleQuickPublishPick = (picked: PickedItem[]) => {
    setPublishPickerOpen(false)
    setPublishTarget(pickedItemToTarget(picked))
  }
  const doQuickPublish = (payload: PublishPayload): Promise<{ data: unknown }> => {
    if (!publishTarget) return Promise.reject(new Error('No content selected'))
    return publishQuickTarget(publishTarget, payload)
  }

  return (
    // The dashboard fills the shell's content column and scrolls vertically
    // when the viewport is too short for everything — sections are shrink-0
    // so nothing gets squeezed or clipped. The screens/groups panel grows to
    // absorb spare height on tall viewports but never shrinks below its
    // content (its lists are paged, so its height stays bounded).
    // -me/pe by the shell's own padding push the scrollbar out to the content
    // panel's edge (instead of floating mid-page next to the cards), and
    // .subtle-scrollbar recolors it into the section's background family.
    // No w-full: the flex-column parent stretches this box, and stretch (not
    // width:100%) is what lets the negative end margin widen it into the
    // shell padding so the scrollbar actually sits at the edge.
    <div className="flex flex-col gap-5 min-w-0 flex-1 min-h-0 overflow-y-auto subtle-scrollbar -me-[var(--space-l)] pe-[var(--space-l)]">
      {/* ── Header toolbar — title lives in the TopBar; here the branch scope
          sits at the start and the actions (Refresh / Quick Add / Quick
          Publish / web player) at the end. */}
      <div className="flex items-center justify-end gap-2 shrink-0">
        {/* Branch filter — scopes the stat cards + screens/groups panel.
            Styled like the Screens page's branch card: the branch name sits
            directly on the background and pressing it opens the list. */}
        <div className="me-auto flex items-center gap-2 min-w-0">
          <MapPin className="w-4 h-4 text-blue-500 shrink-0" />
          <Select value={filterBranch} onValueChange={onBranchChange}>
            <SelectTrigger
              aria-label={t('screens.selectBranch', 'Select branch')}
              className="h-9 w-auto min-h-0 border-0 bg-transparent p-0 shadow-none gap-1.5 font-semibold text-sm focus:ring-0 focus:ring-offset-0 [&>svg]:h-4 [&>svg]:w-4 [&>svg]:opacity-70 truncate"
            >
              <SelectValue>
                {filterBranch === 'all'
                  ? t('screens.allBranches', 'All Branches')
                  : branches.find(b => b.id.toString() === filterBranch)?.name
                    ?? t('screens.allBranches', 'All Branches')}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('screens.allBranches', 'All Branches')}</SelectItem>
              {branches.map(b => (
                <SelectItem key={b.id} value={b.id.toString()}>{b.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          variant="outline"
          size="icon"
          onClick={handleRefresh}
          disabled={refreshing}
          className="h-9 w-9 shrink-0 bg-card"
          title={t('dashboard.refreshAll', 'Refresh All')}
          aria-label={t('dashboard.refreshAll', 'Refresh All')}
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
        </Button>
        {/* Quick Add — create a screen, group, playlist, or audio playlist.
            Publishing actions live in Quick Publish next door. Hidden
            entirely (not just its items) when the user can't do any of them —
            an enabled button opening onto an empty/near-empty menu reads as
            broken rather than as "nothing to do here". */}
        {canQuickAdd && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="gap-2 h-9 px-3 sm:px-4" disabled={quickBusy} aria-label={t('dashboard.quickAdd', 'Quick Add')}>
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">{t('dashboard.quickAdd', 'Quick Add')}</span>
                <ChevronDown className="w-3.5 h-3.5 opacity-80" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              {canCreateScreen && (
                <DropdownMenuItem onClick={() => navigate('/screens/new')} className="gap-2">
                  <Monitor className="w-4 h-4" />
                  {t('dashboard.addScreen', 'Add Screen')}
                </DropdownMenuItem>
              )}
              {canEditScreens && (
                <DropdownMenuItem onClick={() => setCreateOpen(true)} className="gap-2">
                  <Users className="w-4 h-4" />
                  {t('dashboard.createGroup', 'Create Group')}
                </DropdownMenuItem>
              )}
              {(canCreateScreen || canEditScreens) && (canUploadMedia || canCreatePlaylists) && (
                <DropdownMenuSeparator />
              )}
              {canUploadMedia && (
                <DropdownMenuItem onClick={() => openUpload({ onComplete: loadMedia })} className="gap-2">
                  <Upload className="w-4 h-4" />
                  {t('dashboard.uploadContent', 'Upload Content')}
                </DropdownMenuItem>
              )}
              {canCreatePlaylists && (
                <DropdownMenuItem onClick={quickAddPlaylist} className="gap-2">
                  <Film className="w-4 h-4" />
                  {t('dashboard.addPlaylist', 'Create Playlist')}
                </DropdownMenuItem>
              )}
              {canUploadMedia && (
                <DropdownMenuItem onClick={() => openUpload({ accept: 'audio/*', allowAudio: true })} className="gap-2">
                  <Music className="w-4 h-4" />
                  {t('dashboard.addAudio', 'Add Audio')}
                </DropdownMenuItem>
              )}
              {canCreatePlaylists && (
                <DropdownMenuItem onClick={quickAddAudioPlaylist} className="gap-2">
                  <Music className="w-4 h-4" />
                  {t('dashboard.addAudioPlaylist', 'Audio playlist')}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* Quick Publish — pick content / audio / a stop screen / a transition
            and push it to screens/groups. */}
        {canPublish && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="h-9 w-9 shrink-0 bg-card"
                title={t('dashboard.quickPublish', 'Quick Publish')}
                aria-label={t('dashboard.quickPublish', 'Quick Publish')}
              >
                <Upload className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuItem onClick={() => setPublishPickerOpen(true)} className="gap-2">
                <Film className="w-4 h-4" />
                {t('dashboard.publishContent', 'Content')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setQuickPickKind('audio')} className="gap-2">
                <Music className="w-4 h-4" />
                {t('dashboard.publishAudio', 'Audio')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setQuickPickKind('stop_screen')} className="gap-2">
                <MonitorPause className="w-4 h-4" />
                {t('dashboard.publishStopScreen', 'Stop screen')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setQuickPickKind('transition')} className="gap-2">
                <Sparkles className="w-4 h-4" />
                {t('dashboard.publishTransition', 'Transition')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        {/* Opens the browser player in a new tab — a quick way to preview/run
            a screen from any device without the APK. */}
        <Button
          variant="outline"
          size="icon"
          className="h-9 w-9 shrink-0 bg-card"
          onClick={() => window.open(WEB_PLAYER_URL, '_blank', 'noopener,noreferrer')}
          title={t('dashboard.openWebPlayer', 'Open web player ({{host}})', { host: WEB_PLAYER_HOST })}
          aria-label={t('dashboard.openWebPlayer', 'Open web player ({{host}})', { host: WEB_PLAYER_HOST })}
        >
          <Globe className="w-4 h-4" />
        </Button>
      </div>

      {/* ── Stat cards row ──────────────────────────────────────────────────
          Each metric is its own card: Total / Online / Offline screens, then
          Licences and Storage. Matches the reference dashboard's card strip. */}
      {/* Stat strip: a single row at every breakpoint, including phone — a fixed
          2-column phone grid let each card's fr-unit width shrink without a
          floor, which squeezed the icon tile + text until they overflowed
          instead of ever triggering the scroll. Each card now keeps a
          text-safe minimum width and stretches to fill when there's room
          (minmax(…,1fr)); once the cards can't all fit at that minimum — the
          point where labels would start to clip — the row scrolls sideways
          instead. The subtle scrollbar only appears while it actually
          overflows, so the scroll stays discoverable on desktop without
          cluttering the row when everything fits. */}
      <div className="grid grid-flow-col auto-cols-[minmax(11rem,1fr)] overflow-x-auto subtle-scrollbar gap-3 sm:auto-cols-[minmax(14rem,1fr)] sm:gap-4 shrink-0">
        <StatCard
          icon={Monitor}
          tone="neutral"
          value={statsLoading ? null : totalScreens}
          label={t('dashboard.totalScreens', 'Total Screens')}
          hint={t('dashboard.allScreens', 'All screens')}
        />
        <StatCard
          icon={Wifi}
          tone="emerald"
          value={statsLoading ? null : onlineScreens}
          label={t('dashboard.onlineScreens', 'Online screens')}
          hint={t('dashboard.pctOfTotal', { defaultValue: '{{pct}}% of total', pct: pct(onlineScreens, totalScreens) })}
        />
        <StatCard
          icon={WifiOff}
          tone="offline"
          value={statsLoading ? null : offlineScreens}
          label={t('dashboard.offlineScreens', 'Offline screens')}
          hint={t('dashboard.pctOfTotal', { defaultValue: '{{pct}}% of total', pct: pct(offlineScreens, totalScreens) })}
        />
        <StatCard
          icon={BadgeCheck}
          tone="blue"
          // seats_used/seat_count/seats_remaining are org-wide subscription
          // figures. A license pool isn't split per branch on the backend, so
          // there's no real "branch-scoped total" to compute — instead of
          // showing the org-wide pool to a branch-scoped viewer/branch_manager
          // (leaking how many licenses the whole org has), non-admins see only
          // their own branch's screen count, with no org total/remaining
          // attached at all.
          value={fullAccess
            ? <>{licenceSeats.used}<span className="text-muted-foreground"> / {licenceSeats.total}</span></>
            : totalScreens}
          label={t('dashboard.licencesUsed', 'Licences Used')}
          hint={fullAccess
            ? t('dashboard.licencesRemaining', {
                defaultValue: '{{count}} remaining',
                count: Math.max(0, licenceSeats.total - licenceSeats.used),
              })
            : t('dashboard.licencesBranchHint', 'Screens in your branch')}
        />
        <StatCard
          icon={HardDrive}
          tone="violet"
          value={mediaLoading ? null : formatBytes(storageUsed)}
          label={t('dashboard.storageUsed', 'Storage Used')}
          footer={<Progress value={storagePct} className="h-1.5 mt-2" />}
        />
      </div>

      {/* ── Screens / Groups panel — the primary operator surface, so it sits
          directly under the stat cards. Grows to absorb spare height but never
          shrinks below its content (the page scrolls instead), so the paged
          card grid is always fully visible. ─────────────────────────────── */}
      {/* grow (not flex-1): overflow-hidden zeroes the automatic min-height,
          so a basis-0 flex-1 would let short viewports squeeze the panel and
          clip the card rows. grow + shrink-0 keeps basis:auto — the panel
          absorbs spare height but never shrinks below its content; the page
          scrolls instead. */}
      <div className="rounded-2xl bg-card overflow-hidden flex flex-col grow shrink-0">
        <div className="px-4 pt-3 border-b border-border flex items-end gap-1 shrink-0">
          <TabButton
            active={tab === 'screens'}
            onClick={() => setTab('screens')}
            icon={Monitor}
            label={t('dashboard.myScreens', 'Screens')}
            badge={screens.length || undefined}
          />
          <TabButton
            active={tab === 'groups'}
            onClick={() => setTab('groups')}
            icon={Box}
            label={t('dashboard.myGroups', 'Groups')}
            badge={groups.length || undefined}
          />
        </div>

        {/* Toolbar — search only; group creation lives in Quick Add. */}
        <div className="px-4 py-3 flex items-center gap-3 border-b border-border shrink-0">
          <div className="relative flex-1 min-w-0 max-w-md">
            <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={groupSearch}
              onChange={e => onSearchChange(e.target.value)}
              placeholder={tab === 'groups'
                ? t('dashboard.searchGroups', 'Search groups…')
                : t('dashboard.searchScreens', 'Search screens…')
              }
              className="ps-9 h-9"
            />
          </div>
        </div>

        {/* Body — a single page of cards; paging moves through the list. Sized
            by its content (no internal scroll) so no card row is ever cut off;
            the page itself scrolls when the viewport is short. */}
        <div className="p-6 flex-1">
          {tab === 'groups' ? (
            groupsLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-xl" />)}
              </div>
            ) : filteredGroups.length === 0 ? (
              <GroupsEmptyState onCreate={() => setCreateOpen(true)} hasSearch={!!groupSearch.trim()} />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {pagedGroups.map(g => (
                  <GroupCard
                    key={g.id}
                    group={g}
                    onOpen={() => setEditTarget(g)}
                    onEdit={() => setEditTarget(g)}
                    onDelete={() => setDeleteTarget(g)}
                  />
                ))}
              </div>
            )
          ) : (
            statsLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-xl" />)}
              </div>
            ) : filteredScreens.length === 0 ? (
              <ScreensEmptyState onAdd={canCreateScreen ? () => navigate('/screens/new') : undefined} hasSearch={!!groupSearch.trim()} />
            ) : (
              <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {pagedScreens.map(s => (
                  <ScreenCard
                    key={s.id}
                    screen={s}
                    onOpen={() => navigate(`/screens/${s.public_id}`)}
                    onInfo={() => setInfoTarget(s)}
                  />
                ))}
              </ul>
            )
          )}
        </div>

        {/* Pager — fixed footer; only shown once there's a loaded, non-empty list. */}
        {tab === 'groups' && !groupsLoading && filteredGroups.length > 0 && (
          <PaginationBar
            page={gPage}
            totalPages={groupsTotalPages}
            totalItems={filteredGroups.length}
            pageSize={PAGE_SIZE}
            onPrev={() => setGroupsPage(p => Math.max(1, p - 1))}
            onNext={() => setGroupsPage(p => Math.min(groupsTotalPages, p + 1))}
          />
        )}
        {tab === 'screens' && !statsLoading && filteredScreens.length > 0 && (
          <PaginationBar
            page={sPage}
            totalPages={screensTotalPages}
            totalItems={filteredScreens.length}
            pageSize={PAGE_SIZE}
            onPrev={() => setScreensPage(p => Math.max(1, p - 1))}
            onNext={() => setScreensPage(p => Math.min(screensTotalPages, p + 1))}
          />
        )}
      </div>

      {/* ── Recent Assets + Activity ────────────────────────────────────────
          Secondary panels, now below the screens/groups panel: the media
          library's latest uploads and the live activity feed. */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 shrink-0">
        <RecentAssetsCard
          assets={recentAssets}
          loading={mediaLoading}
          onViewAll={() => navigate('/management/media')}
        />
        <ActivityCard
          activity={recentActivity}
          onViewAll={() => navigate('/activity')}
        />
      </div>

      {/* ── Dialogs ───────────────────────────────────────────────────── */}
      {createOpen && (
        <ScreenGroupDialog
          group={null}
          allScreens={screens}
          onSave={handleCreateGroup}
          onClose={() => setCreateOpen(false)}
        />
      )}
      {editTarget && (
        <ScreenGroupDialog
          group={editTarget}
          allScreens={screens}
          onSave={handleEditGroup}
          onClose={() => setEditTarget(null)}
        />
      )}
      <ConfirmDialog
        open={!!deleteTarget}
        title={t('group.deleteTitle', 'Delete Group')}
        description={t('group.deleteDesc', 'This group will be removed. The screens it contains are not deleted.')}
        onConfirm={handleDeleteGroup}
        onClose={() => setDeleteTarget(null)}
      />

      {/* Quick Publish (content) — step 1: pick a media item / link / playlist.
          Same tab set as the Content Hub's "Publish content" picker. */}
      <PlaylistContentPicker
        open={publishPickerOpen}
        tabs={['all', 'media', 'documents', 'links', 'playlists']}
        confirmLabel={t('dashboard.quickPublishPick', 'Choose to publish')}
        onClose={() => setPublishPickerOpen(false)}
        onConfirm={handleQuickPublishPick}
      />
      {/* Quick Publish (audio / stop screen / transition) — step 1: pick one. */}
      {quickPickKind && (
        <QuickPickDialog
          kind={quickPickKind}
          onClose={() => setQuickPickKind(null)}
          onPick={target => { setQuickPickKind(null); setPublishTarget(target) }}
        />
      )}
      {/* Quick Publish — step 2: pick the screens/groups + publish now. */}
      {publishTarget && (
        <PublishDialog
          open
          mode="publish"
          kind={publishDialogKind(publishTarget)}
          publish={doQuickPublish}
          onClose={() => setPublishTarget(null)}
          onPublished={() => setPublishTarget(null)}
        />
      )}
      {/* Right-side screen info drawer — the same panel as the Screens page,
          opened from a dashboard screen card's info button. */}
      <ScreenInfoDrawer
        open={infoTarget !== null}
        publicId={infoTarget?.public_id ?? null}
        onClose={() => setInfoTarget(null)}
      />
    </div>
  )
}

// ── QuickPickDialog ──────────────────────────────────────────────────────
// Step-1 picker for the Quick Publish flavours the content picker doesn't
// cover: one audio source (audio playlists + audio files), one stop screen,
// or one transition. Clicking a row hands the pick straight to PublishDialog.
function QuickPickDialog({
  kind, onClose, onPick,
}: {
  kind: QuickPickKind
  onClose: () => void
  onPick: (target: QuickPublishTarget) => void
}) {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(kind !== 'transition')
  const [search, setSearch] = useState('')
  const [audios, setAudios] = useState<{ id: number; original_name: string }[]>([])
  const [audioPlaylists, setAudioPlaylists] = useState<{ id: number; name: string }[]>([])
  const [stopScreens, setStopScreens] = useState<{ id: number; name: string }[]>([])

  useEffect(() => {
    if (kind === 'transition') return
    setLoading(true)
    const load = kind === 'audio'
      ? Promise.all([getAudioPlaylists(), getAudios()])
          .then(([p, a]) => { setAudioPlaylists(p.data || []); setAudios(a.data || []) })
      : getStopScreens().then(r => setStopScreens(r.data || []))
    load
      .catch(() => toast.error(t('common.error', 'Something went wrong')))
      .finally(() => setLoading(false))
  }, [kind, t])

  const q = search.trim().toLowerCase()
  const match = (name: string) => !q || name.toLowerCase().includes(q)

  type Section = {
    label?: string
    rows: { key: string; label: string; sub?: string; target: QuickPublishTarget }[]
  }
  const sections: Section[] = kind === 'audio'
    ? [
        {
          label: t('companions.audioPlaylists', 'Audio playlists'),
          rows: audioPlaylists.filter(p => match(p.name)).map(p => ({
            key: `p:${p.id}`, label: p.name, target: { kind: 'audio_playlist', id: p.id },
          })),
        },
        {
          label: t('companions.audioFiles', 'Audio files'),
          rows: audios.filter(a => match(a.original_name)).map(a => ({
            key: `a:${a.id}`, label: a.original_name, target: { kind: 'audio', id: a.id },
          })),
        },
      ]
    : kind === 'stop_screen'
      ? [{
          rows: stopScreens.filter(s => match(s.name)).map(s => ({
            key: String(s.id), label: s.name, target: { kind: 'stop_screen', id: s.id },
          })),
        }]
      : [{
          rows: TRANSITIONS.filter(tr => match(tr.name)).map(tr => ({
            key: tr.key, label: tr.name, sub: tr.category, target: { kind: 'transition', key: tr.key },
          })),
        }]
  const isEmpty = sections.every(s => s.rows.length === 0)

  const Icon = kind === 'audio' ? Music : kind === 'stop_screen' ? MonitorPause : Sparkles
  const title = kind === 'audio'
    ? t('dashboard.pickAudioTitle', 'Publish audio')
    : kind === 'stop_screen'
      ? t('dashboard.pickStopScreenTitle', 'Publish stop screen')
      : t('dashboard.pickTransitionTitle', 'Apply transition')
  const description = kind === 'audio'
    ? t('dashboard.pickAudioDesc', 'Choose an audio playlist or audio file to stream to screens.')
    : kind === 'stop_screen'
      ? t('dashboard.pickStopScreenDesc', 'Choose a stop screen to layer over screens while they are idle.')
      : t('dashboard.pickTransitionDesc', 'Choose how content animates between items.')

  return (
    <Dialog open onOpenChange={o => { if (!o) onClose() }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className="w-4 h-4 text-primary" />
            {title}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={t('common.search', 'Search…')}
            className="ps-9 h-9"
          />
        </div>

        <div className="max-h-72 overflow-y-auto rounded-md border border-border bg-card">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : isEmpty ? (
            <p className="text-xs text-muted-foreground text-center py-6">
              {t('common.noData', 'No data found')}
            </p>
          ) : (
            sections.filter(s => s.rows.length > 0).map((section, i) => (
              <div key={section.label ?? i}>
                {section.label && (
                  <p className="px-3 pt-2 pb-1 text-[11px] font-medium text-muted-foreground">{section.label}</p>
                )}
                <ul className="divide-y divide-border">
                  {section.rows.map(row => (
                    <li key={row.key}>
                      <button
                        type="button"
                        onClick={() => onPick(row.target)}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-start hover:bg-muted/40 transition-colors"
                      >
                        <Icon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                        <span className="truncate flex-1">{row.label}</span>
                        {row.sub && <span className="text-[11px] text-muted-foreground shrink-0">{row.sub}</span>}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

// ── StatCard ─────────────────────────────────────────────────────────────
// Single-metric card: a soft-tinted icon tile, the value, a label, and an
// optional hint line / footer (used by Storage for its progress bar).
const TONES: Record<string, { tile: string; icon: string }> = {
  neutral: { tile: 'bg-muted',            icon: 'text-foreground' },
  emerald: { tile: 'bg-emerald-500/10',   icon: 'text-emerald-500' },
  offline: { tile: 'bg-destructive/10',   icon: 'text-destructive' },
  blue:    { tile: 'bg-blue-500/10',      icon: 'text-blue-500' },
  violet:  { tile: 'bg-violet-500/10',    icon: 'text-violet-500' },
}

function StatCard({
  icon: Icon, tone, value, label, hint, footer,
}: {
  icon: React.ElementType
  tone: keyof typeof TONES
  value: React.ReactNode | null
  label: string
  hint?: string
  footer?: React.ReactNode
}) {
  const c = TONES[tone] ?? TONES.neutral
  // Horizontal layout mirrors the Screens page StatCard (icon tile on the
  // start, label/value/hint stacked beside it) so the two pages read as one
  // system. The optional footer (Storage's progress bar) sits full-width below.
  return (
    <div className="rounded-2xl bg-card p-5 flex flex-col gap-3 min-h-[92px]">
      <div className="flex items-center gap-4">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${c.tile}`}>
          <Icon className={`w-6 h-6 ${c.icon}`} />
        </div>
        <div className="min-w-0">
          <p className="text-xs font-medium text-muted-foreground truncate">{label}</p>
          <p className="text-2xl font-bold text-foreground leading-tight tabular-nums">
            {value === null ? <span className="text-muted-foreground/60">—</span> : value}
          </p>
          {hint && <p className="text-[11px] text-muted-foreground truncate mt-0.5">{hint}</p>}
        </div>
      </div>
      {footer}
    </div>
  )
}

// Whole-number percentage of `part` within `total` (0 when total is 0).
function pct(part: number, total: number) {
  return total > 0 ? Math.round((part / total) * 100) : 0
}

// ── Recent Assets card ───────────────────────────────────────────────────
// Latest uploads from the media library, with a tiny thumbnail (images) or a
// type icon (video / audio / pdf / font). Rows deep-link to the Media Hub.
function assetIcon(fileType: string) {
  if (fileType.startsWith('image/')) return ImageIcon
  if (fileType.startsWith('video/')) return Film
  if (fileType.startsWith('audio/')) return Music
  if (fileType === 'application/pdf') return FileText
  if (fileType.startsWith('font/') || fileType.includes('font')) return TypeIcon
  return FileText
}

// Assets shown per page inside the Recent Assets card before the pager kicks in.
const ASSETS_PAGE_SIZE = 3

function RecentAssetsCard({
  assets, loading, onViewAll,
}: {
  assets: MediaItem[]
  loading: boolean
  onViewAll: () => void
}) {
  const { t } = useTranslation()
  const [page, setPage] = useState(1)
  const totalPages = Math.max(1, Math.ceil(assets.length / ASSETS_PAGE_SIZE))
  const curPage    = Math.min(page, totalPages)
  const pageItems  = assets.slice((curPage - 1) * ASSETS_PAGE_SIZE, curPage * ASSETS_PAGE_SIZE)
  return (
    <div className="lg:col-span-2 rounded-2xl bg-card overflow-hidden flex flex-col">
      <div className="px-4 py-3 border-b border-border flex items-center justify-between">
        <h2 className="text-sm font-semibold flex items-center gap-2">
          <ImageIcon className="w-4 h-4 text-primary" />
          {t('dashboard.recentAssets', 'Recent Assets')}
        </h2>
        <button
          type="button"
          onClick={onViewAll}
          className="text-xs font-medium text-primary hover:underline"
        >
          {t('dashboard.viewAll', 'View all')}
        </button>
      </div>
      <div className="p-2 flex-1">
        {loading ? (
          <div className="space-y-1">
            {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-12 rounded-lg" />)}
          </div>
        ) : assets.length === 0 ? (
          <p className="text-center text-sm text-muted-foreground py-8">
            {t('dashboard.noAssets', 'No assets uploaded yet')}
          </p>
        ) : (
          <ul className="space-y-2">
            {pageItems.map(a => {
              const Icon = assetIcon(a.file_type)
              const isImage = a.file_type.startsWith('image/')
              return (
                <li key={a.id}>
                  <button
                    type="button"
                    onClick={onViewAll}
                    className="w-full text-start flex items-center gap-3 px-2 py-2 rounded-lg border card-inner hover:border-primary/30 transition-colors"
                  >
                    <span className="w-9 h-9 rounded-lg bg-card border border-border/60 flex items-center justify-center shrink-0 overflow-hidden">
                      {isImage
                        ? <img src={a.url} alt="" className="w-full h-full object-cover" loading="lazy" />
                        : <Icon className="w-4 h-4 text-muted-foreground" />}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{a.original_name}</p>
                      <p className="text-[11px] text-muted-foreground truncate">
                        {a.file_type.split('/')[1]?.toUpperCase() || a.file_type}
                        {a.file_size ? ` · ${formatBytes(a.file_size)}` : ''}
                      </p>
                    </div>
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </div>
      {!loading && assets.length > 0 && (
        <CardPager
          page={curPage}
          totalPages={totalPages}
          onPrev={() => setPage(p => Math.max(1, p - 1))}
          onNext={() => setPage(p => Math.min(totalPages, p + 1))}
        />
      )}
    </div>
  )
}

// ── Activity card ────────────────────────────────────────────────────────
// Live task feed (publishes, uploads, changes) shared with the Task Center.
// Activity rows per page inside the dashboard Activity card.
const ACTIVITY_PAGE_SIZE = 3

function ActivityCard({
  activity, onViewAll,
}: {
  activity: import('@meno/ui/lib/api').Task[]
  onViewAll: () => void
}) {
  const { t } = useTranslation()
  const [page, setPage] = useState(1)
  const totalPages = Math.max(1, Math.ceil(activity.length / ACTIVITY_PAGE_SIZE))
  const curPage    = Math.min(page, totalPages)
  const pageItems  = activity.slice((curPage - 1) * ACTIVITY_PAGE_SIZE, curPage * ACTIVITY_PAGE_SIZE)
  return (
    <div className="rounded-2xl bg-card overflow-hidden flex flex-col">
      <div className="px-4 py-3 border-b border-border flex items-center justify-between">
        <h2 className="text-sm font-semibold flex items-center gap-2">
          <ActivityIcon className="w-4 h-4 text-primary" />
          {t('dashboard.activity', 'Activity')}
        </h2>
        <button
          type="button"
          onClick={onViewAll}
          className="text-xs font-medium text-primary hover:underline"
        >
          {t('dashboard.viewAll', 'View all')}
        </button>
      </div>
      <div className="p-3 flex-1">
        {activity.length === 0 ? (
          <p className="text-center text-sm text-muted-foreground py-8">
            {t('dashboard.noActivity', 'No recent activity')}
          </p>
        ) : (
          <div className="space-y-2">
            {pageItems.map(task => <TaskRow key={task.id} task={task} />)}
          </div>
        )}
      </div>
      {activity.length > 0 && (
        <CardPager
          page={curPage}
          totalPages={totalPages}
          onPrev={() => setPage(p => Math.max(1, p - 1))}
          onNext={() => setPage(p => Math.min(totalPages, p + 1))}
        />
      )}
    </div>
  )
}

// ── CardPager ────────────────────────────────────────────────────────────
// Compact footer pager for the dashboard's Recent Assets / Activity cards.
// Chevrons flip in RTL so "previous" always points back toward the start.
function CardPager({
  page, totalPages, onPrev, onNext,
}: {
  page: number
  totalPages: number
  onPrev: () => void
  onNext: () => void
}) {
  const { t, i18n } = useTranslation()
  const Prev = ChevronLeft
  const Next = ChevronRight
  return (
    <div className="px-3 py-2 border-t border-border flex items-center justify-between shrink-0">
      <span className="text-[11px] tabular-nums text-muted-foreground">{page} / {totalPages}</span>
      <div className="flex items-center gap-1.5">
        <Button
          variant="outline"
          size="sm"
          className="h-7 w-7 p-0"
          disabled={page <= 1}
          onClick={onPrev}
          aria-label={t('common.previous', 'Previous')}
        >
          <Prev className="w-4 h-4" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="h-7 w-7 p-0"
          disabled={page >= totalPages}
          onClick={onNext}
          aria-label={t('common.next', 'Next')}
        >
          <Next className="w-4 h-4" />
        </Button>
      </div>
    </div>
  )
}

// ── PaginationBar ────────────────────────────────────────────────────────
// Fixed footer that pages through the screens / groups grid. Chevrons flip in
// RTL so "previous" always points back toward the start.
function PaginationBar({
  page, totalPages, totalItems, pageSize, onPrev, onNext,
}: {
  page: number
  totalPages: number
  totalItems: number
  pageSize: number
  onPrev: () => void
  onNext: () => void
}) {
  const { t, i18n } = useTranslation()
  const from = totalItems === 0 ? 0 : (page - 1) * pageSize + 1
  const to   = Math.min(page * pageSize, totalItems)
  const Prev = ChevronLeft
  const Next = ChevronRight
  return (
    <div className="px-4 py-2.5 border-t border-border flex items-center justify-between shrink-0">
      <p className="text-[11px] text-muted-foreground tabular-nums">
        {t('dashboard.showingRange', {
          defaultValue: 'Showing {{from}}–{{to}} of {{total}}',
          from, to, total: totalItems,
        })}
      </p>
      <div className="flex items-center gap-1.5">
        <Button
          variant="outline"
          size="sm"
          className="h-7 w-7 p-0"
          disabled={page <= 1}
          onClick={onPrev}
          aria-label={t('common.previous', 'Previous')}
        >
          <Prev className="w-4 h-4" />
        </Button>
        <span className="text-[11px] tabular-nums text-muted-foreground px-1 min-w-[3rem] text-center">
          {page} / {totalPages}
        </span>
        <Button
          variant="outline"
          size="sm"
          className="h-7 w-7 p-0"
          disabled={page >= totalPages}
          onClick={onNext}
          aria-label={t('common.next', 'Next')}
        >
          <Next className="w-4 h-4" />
        </Button>
      </div>
    </div>
  )
}

// ── GroupCard ────────────────────────────────────────────────────────────
// Compact tile that shows the group's name, screen count, online ratio,
// and a 3-dot menu for edit/delete. Click anywhere else opens the group
// (today this opens the edit dialog — a real detail page is a follow-up).
function GroupCard({
  group, onOpen, onEdit, onDelete,
}: {
  group: ScreenGroupRow
  onOpen: () => void
  onEdit: () => void
  onDelete: () => void
}) {
  const { t, i18n } = useTranslation()
  const ratio = group.total_count > 0
    ? Math.round((group.online_count / group.total_count) * 100)
    : 0
  const Chevron = ChevronRight
  const stop = (fn: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    fn()
  }
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') onOpen() }}
      className="group relative rounded-xl border card-inner hover:shadow-md hover:border-primary/30 transition-all p-4 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/30"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-card flex items-center justify-center shrink-0">
          <Box className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold truncate">{group.name}</p>
          {group.description && (
            <p className="text-[11px] text-muted-foreground truncate">{group.description}</p>
          )}
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger
            aria-label={t('common.more', 'More')}
            onClick={e => e.stopPropagation()}
            className="w-7 h-7 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors shrink-0"
          >
            <MoreHorizontal className="w-4 h-4" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-40" onClick={e => e.stopPropagation()}>
            <DropdownMenuItem onClick={stop(onEdit)} className="gap-2">
              <Pencil className="w-4 h-4" />
              {t('common.edit', 'Edit')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={stop(onDelete)} className="gap-2 text-destructive focus:text-destructive">
              <Trash2 className="w-4 h-4" />
              {t('common.delete', 'Delete')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Footer — screen count + online ratio + chevron */}
      <div className="mt-3 flex items-center justify-between text-[11px]">
        <span className="flex items-center gap-1.5 text-muted-foreground">
          <Monitor className="w-3 h-3" />
          {group.total_count} {t('dashboard.screensLower', 'screens')}
        </span>
        <span className="flex items-center gap-1.5">
          <span className={`inline-block w-1.5 h-1.5 rounded-full ${
            group.online_count > 0 ? 'bg-emerald-500' : 'bg-muted-foreground/40'
          }`} />
          <span className="text-muted-foreground">
            {group.online_count}/{group.total_count} {t('common.online', 'online')}
          </span>
        </span>
      </div>

      {/* Progress bar — quick visual on group health */}
      <div className="mt-2 h-1 rounded-full bg-muted overflow-hidden">
        <div
          className="h-full bg-emerald-500 transition-all"
          style={{ width: `${ratio}%` }}
        />
      </div>

      <Chevron className="absolute top-1/2 -translate-y-1/2 end-3 w-4 h-4 text-muted-foreground/0 group-hover:text-muted-foreground transition-colors" />
    </div>
  )
}

// ── ScreenCard ───────────────────────────────────────────────────────────
function ScreenCard({ screen, onOpen, onInfo }: { screen: ScreenItem; onOpen: () => void; onInfo: () => void }) {
  const { t } = useTranslation()
  const isVertical = screen.orientation_type === 'vertical'
  const Icon = isVertical ? Smartphone : Monitor
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') onOpen() }}
      className="group text-start rounded-xl border card-inner hover:shadow-md hover:border-primary/30 transition-all p-4 flex items-center gap-3 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/30"
    >
      <div className="w-10 h-10 rounded-xl bg-card flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5 text-muted-foreground" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold truncate">{screen.name}</p>
        {screen.location && (
          <p className="text-[11px] text-muted-foreground truncate">{screen.location}</p>
        )}
      </div>
      {/* Shared status logic/presentation — see ScreenStatusBadge.tsx. Every
          surface (dashboard, screens list, screen details) renders THIS same
          component so a screen's status (incl. license expiring/expired)
          never disagrees between pages. */}
      <ScreenStatusBadge screen={screen} className="shrink-0" title={screen.disconnect_at ?? undefined} />
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 shrink-0"
        title={t('screens.moreInfo', 'More info')}
        aria-label={t('screens.moreInfo', 'More info')}
        onClick={e => { e.stopPropagation(); onInfo() }}
      >
        <Info className="h-4 w-4" />
      </Button>
    </div>
  )
}

// ── Empty states ─────────────────────────────────────────────────────────
function GroupsEmptyState({ onCreate, hasSearch }: { onCreate: () => void; hasSearch: boolean }) {
  const { t } = useTranslation()
  if (hasSearch) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <p className="text-sm">{t('common.noData', 'No data found')}</p>
      </div>
    )
  }
  return (
    <div className="flex flex-col items-center justify-center text-center py-10 gap-3">
      {/* Stacked-cards illustration mirroring the design reference */}
      <div className="relative w-44 h-32">
        <div className="absolute inset-0 rounded-full bg-muted/40" />
        <div className="absolute top-3 start-4 w-32 h-7 rounded-md bg-card border border-border shadow-sm flex items-center gap-2 px-2">
          <div className="w-4 h-4 rounded-sm bg-muted" />
          <div className="flex-1 h-1.5 rounded-full bg-muted" />
        </div>
        <div className="absolute top-12 start-10 w-32 h-7 rounded-md bg-card border border-border shadow-sm flex items-center gap-2 px-2">
          <div className="w-4 h-4 rounded-sm bg-muted" />
          <div className="flex-1 h-1.5 rounded-full bg-muted" />
        </div>
        <div className="absolute top-[5.25rem] start-4 w-32 h-7 rounded-md bg-card border border-border shadow-sm flex items-center gap-2 px-2">
          <div className="w-4 h-4 rounded-sm bg-muted" />
          <div className="flex-1 h-1.5 rounded-full bg-muted" />
        </div>
      </div>
      <h3 className="text-base font-semibold text-foreground">
        {t('dashboard.groupsEmptyTitle', "You haven't created any groups yet")}
      </h3>
      <p className="text-sm text-muted-foreground max-w-md">
        {t('dashboard.groupsEmptyDesc', 'Organize your screens into groups for easier access and more efficient management. Click the "New Group" button to get started now!')}
      </p>
      <Button onClick={onCreate} className="gap-2 mt-2">
        <Plus className="w-4 h-4" />
        {t('dashboard.newGroup', 'New Group')}
      </Button>
    </div>
  )
}

